"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FakeVideosClient = void 0;
const BoclipsApiErrorFactory_1 = require("../../../test-support/BoclipsApiErrorFactory");
const LinkEntity_1 = require("../../common/model/LinkEntity");
const PageableFactory_1 = require("../../common/model/PageableFactory");
class FakeVideosClient {
    constructor(contentPackagesClient) {
        this.videos = [];
        this.validReferers = [];
        this.videoCaptions = [];
        this.facets = {
            ageRanges: [],
            bestForTags: [],
            subjects: [],
            durations: [],
            resourceTypes: [],
            videoTypes: [],
            channels: [],
            educationLevels: [],
            topics: [],
            languages: [],
            cefrLevels: [],
            videoSubtypes: [],
        };
        this.recommendations = {};
        this.transcripts = {};
        this.contentPackagesClient = contentPackagesClient;
    }
    get(id, referer) {
        const video = this.videos.find((video) => video.id === id);
        if (video === undefined) {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({ status: 404 }));
        }
        return !referer || this.validReferers.includes(referer)
            ? Promise.resolve(video)
            : Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({ status: 404 }));
    }
    getVideoProjection() {
        return Promise.resolve(this.videos[0]);
    }
    search(searchRequest) {
        const matchingVideos = this.videos.filter((video) => {
            const channelCodes = video.taxonomy?.channel?.categories.map((category) => category.codeValue) ?? [];
            const manualCodes = video.taxonomy?.manual?.categories.map((category) => category.codeValue) ?? [];
            const allCodes = channelCodes.concat(manualCodes);
            if (searchRequest.channel &&
                searchRequest.channel.length > 0 &&
                searchRequest.channel?.find((channelId) => channelId !== video.channelId)) {
                return false;
            }
            if (searchRequest.id &&
                searchRequest.id.length > 0 &&
                !searchRequest.id?.includes(video.id)) {
                return false;
            }
            if (searchRequest.subject &&
                searchRequest.subject.length > 0 &&
                searchRequest.subject?.some((subject) => !video.subjects.map((s) => s.id).includes(subject))) {
                return false;
            }
            if (searchRequest.query &&
                searchRequest.query.length > 0 &&
                video.title.indexOf(searchRequest.query) === -1 &&
                video.description.indexOf(searchRequest.query) === -1) {
                return false;
            }
            if (searchRequest.type &&
                searchRequest.type.length > 0 &&
                searchRequest.type.some((videoType) => video.type !== videoType)) {
                return false;
            }
            if (searchRequest.best_for &&
                searchRequest.best_for.length > 0 &&
                searchRequest.best_for.some((bestForTag) => !video.bestFor?.map((t) => t.label).includes(bestForTag))) {
                return false;
            }
            if (searchRequest.released_date_from &&
                searchRequest.released_date_from.length > 0 &&
                video.releasedOn <= new Date(searchRequest.released_date_from)) {
                return false;
            }
            if (searchRequest.released_date_to &&
                searchRequest.released_date_to.length > 0 &&
                new Date(searchRequest.released_date_to) <= video.releasedOn) {
                return false;
            }
            if (searchRequest.category_code &&
                searchRequest.category_code.length > 0 &&
                searchRequest.category_code.some((code) => !allCodes?.includes(code))) {
                return false;
            }
            if (searchRequest.education_level &&
                searchRequest.education_level.length > 0 &&
                searchRequest.education_level.every((code) => !video.educationLevels?.map((level) => level.code).includes(code))) {
                return false;
            }
            if (searchRequest.topics &&
                searchRequest.topics.length > 0 &&
                searchRequest.topics.some((topic) => !video.topics?.includes(topic))) {
                return false;
            }
            if (searchRequest.subtype &&
                searchRequest.subtype.length > 0 &&
                searchRequest.subtype.some((type) => !video.contentCategories
                    ?.map((contentCategory) => contentCategory.key)
                    .includes(type))) {
                return false;
            }
            if (searchRequest.content_package &&
                searchRequest.content_package.length > 0) {
                const contentPackage = this.contentPackagesClient?.getForFake(searchRequest.content_package);
                if (!contentPackage) {
                    return false;
                }
                //if we have a content package we just want to exclude videos not in it - we prioritise other filters
                if (contentPackage
                    .accessRules.find((rule) => rule.type === 'IncludedVideos')
                    .videoIds.indexOf(video.id) == -1) {
                    return false;
                }
            }
            if (searchRequest.language &&
                searchRequest.language.length > 0 &&
                searchRequest.language.some((language) => video.language?.code !== language)) {
                return false;
            }
            if (searchRequest.cefr_level &&
                searchRequest.cefr_level.length > 0 &&
                searchRequest.cefr_level.every((cefrLevel) => video.cefrLevel !== cefrLevel)) {
                return false;
            }
            return true;
        });
        const pageableVideos = PageableFactory_1.PageableFactory.sample(matchingVideos, {
            number: searchRequest.page,
            size: searchRequest.size,
        });
        const searchResults = {
            ...pageableVideos,
            facets: this.facets,
        };
        return Promise.resolve(searchResults);
    }
    async update(id, updateVideoRequest) {
        const videoIndex = await this.findVideoIndexById(id);
        const { title, description, additionalDescription, subjectIds, contentWarningIds, ageRangeMin, ageRangeMax, tagIds, categories, educationLevels, } = updateVideoRequest;
        const originalVideo = this.videos[videoIndex];
        this.videos[videoIndex] = {
            ...originalVideo,
            title: title ? title : originalVideo.title,
            description: description ? description : originalVideo.description,
            additionalDescription: additionalDescription
                ? additionalDescription
                : originalVideo.additionalDescription,
            ageRange: ageRangeMin || ageRangeMax
                ? { min: ageRangeMin, max: ageRangeMax }
                : originalVideo.ageRange,
            subjects: subjectIds
                ? subjectIds.map((id) => ({ id, name: `subject${id}` }))
                : originalVideo.subjects,
            contentWarnings: contentWarningIds
                ? contentWarningIds.map((id) => ({ id, label: `warning${id}` }))
                : originalVideo.contentWarnings,
            bestFor: tagIds?.map((tag) => ({ label: `tag-${tag}` })),
            taxonomy: {
                channel: {
                    categories: [
                        {
                            codeValue: 'A',
                            description: 'this is channel description',
                        },
                    ],
                },
                manual: {
                    categories: categories
                        ? categories.map((it) => ({
                            codeValue: it,
                            description: `this is ${it} description`,
                        }))
                        : [{ codeValue: 'A', description: 'test' }],
                },
            },
            educationLevels: educationLevels?.map((level) => ({
                code: level,
                label: 'Label ' + level,
            })),
        };
        return Promise.resolve(this.videos[videoIndex]);
    }
    insertVideo(video) {
        this.videos.push(video);
    }
    setFacets(facets) {
        this.facets = facets;
    }
    clear() {
        this.videos = [];
        this.validReferers = [];
        this.recommendations = {};
    }
    getCaptions(id) {
        const captions = this.videoCaptions.find((it) => it.videoId === id);
        return captions === undefined
            ? Promise.reject('Not found')
            : Promise.resolve({ content: captions.content });
    }
    updateCaptions(id, updateCaptionsRequest) {
        const captionIndex = this.videoCaptions.findIndex((it) => it.videoId === id);
        if (captionIndex !== -1) {
            this.videoCaptions[captionIndex].content = updateCaptionsRequest.captions;
            return Promise.resolve(updateCaptionsRequest.captions);
        }
        else {
            return Promise.reject('Not found');
        }
    }
    addCaptions(videoId, captionContent) {
        this.videoCaptions.push({ videoId: videoId, content: captionContent });
    }
    async deleteThumbnail(video) {
        const videoIndex = await this.findVideoIndexById(video.id);
        this.videos[videoIndex].playback.links = {
            ...this.videos[videoIndex].playback.links,
            thumbnail: new LinkEntity_1.Link({ href: 'thumbnailAfterDelete' }),
            deleteThumbnail: undefined,
            setThumbnailBySecond: new LinkEntity_1.Link({ href: `setThumbnailBySecond` }),
            setCustomThumbnail: new LinkEntity_1.Link({ href: `setCustomThumbnail` }),
        };
        return Promise.resolve(this.videos[videoIndex]);
    }
    async setThumbnailBySecond(video, second) {
        const videoIndex = await this.findVideoIndexById(video.id);
        this.videos[videoIndex].playback.links = {
            ...this.videos[videoIndex].playback.links,
            thumbnail: new LinkEntity_1.Link({ href: `thumbnailAt${second}` }),
            setThumbnailBySecond: undefined,
            setCustomThumbnail: undefined,
            deleteThumbnail: new LinkEntity_1.Link({ href: `deleteThumbnail` }),
        };
        return Promise.resolve(this.videos[videoIndex]);
    }
    async setCustomThumbnail(video, file) {
        if (file.name === 'error-file.jpg') {
            return Promise.reject('Error uploading file');
        }
        const videoIndex = await this.findVideoIndexById(video.id);
        this.videos[videoIndex].playback.links = {
            ...this.videos[videoIndex].playback.links,
            thumbnail: new LinkEntity_1.Link({ href: `defaultThumbnail_${file.name}` }),
            setThumbnailBySecond: undefined,
            setCustomThumbnail: undefined,
            deleteThumbnail: new LinkEntity_1.Link({ href: `deleteThumbnail` }),
        };
        return Promise.resolve(this.videos[videoIndex]);
    }
    addValidReferer(referer) {
        this.validReferers.push(referer);
    }
    updateFromCsv(file) {
        if (file.name === 'bad-file.csv') {
            return Promise.reject('File contains invalid Video IDs on rows 2');
        }
        return Promise.resolve('Success');
    }
    getRecommendations(video) {
        return Promise.resolve(this.recommendations[video.id] || []);
    }
    setRecommendationsForVideo(id, recommendations) {
        this.recommendations[id] = recommendations;
    }
    createEmbedCode(_video, _segmentStart, _segmentEnd) {
        return Promise.resolve({
            embed: "<iframe src='https://api.staging-boclips.com/v1/embeds/embed-id/view'/>",
        });
    }
    setTranscriptForVideo(id, transcript) {
        this.transcripts[id] = transcript;
    }
    getTranscript(video) {
        if (!!this.transcripts[video.id]) {
            return Promise.resolve(this.transcripts[video.id]);
        }
        return Promise.reject("Transcript doesn't exist");
    }
    findVideoIndexById(id) {
        const videoIndex = this.videos.findIndex((video) => video.id === id);
        return videoIndex < 0
            ? Promise.reject(`No video found with id: ${id}`)
            : Promise.resolve(videoIndex);
    }
}
exports.FakeVideosClient = FakeVideosClient;
//# sourceMappingURL=FakeVideosClient.js.map